import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import Cookies from 'js-cookie'
import API_BASE_URL from '../../../../config/api'
import Topbar from '../../mini-comp/Topbar'
import Sidebar from '../../mini-comp/Sidebar'
import { RiEye2Line, RiPrinterLine, RiSearchLine } from 'react-icons/ri'
import Swal from 'sweetalert2'
import { FaRedoAlt, FaCheck } from 'react-icons/fa'

const Payslips = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const [payslips, setPayslips] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [payrollStatus, setPayrollStatus] = useState(null)
    const [processingPayrollId, setProcessingPayrollId] = useState(null)

    useEffect(() => {
        const fetchPayslipData = async () => {
            setLoading(true)
            try {
                const token = Cookies.get('token')
                
                // Fetch payslips
                const res = await axios.get(`${API_BASE_URL}/api/v1/payroll/runs/${id}/payslips/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                console.log(res.data)
                setPayslips(Array.isArray(res.data) ? res.data : [])
                
                // Fetch payroll run details to get status
                try {
                    const payrollRes = await axios.get(`${API_BASE_URL}/api/v1/payroll/runs/${id}/`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    })
                    setPayrollStatus(payrollRes.data.status)
                } catch (err) {
                    console.error("Error fetching payroll run status:", err)
                }
            } catch (err) {
                console.error("Error fetching payslip list:", err)
                setError("Failed to load payslips for this run.")
            } finally {
                setLoading(false)
            }
        }
        fetchPayslipData()
    }, [id])

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0)
    }

    const formatDate = (dateString) => {
        if (!dateString) return '---'
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }

    const filteredPayrolls = payslips.filter(ps =>
        ps.employee_data?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ps.employee_data?.employee_id?.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const handleRerunPayroll = async () => {
        const result = await Swal.fire({
            title: 'Re-run Payroll?',
            text: 'This will re-process the payroll run. Are you sure?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#007bff',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, re-run it',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        setProcessingPayrollId(id);
        try {
            const token = Cookies.get('token');
            // Get current month in YYYY-MM-01 format
            const now = new Date();
            const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
            
            await axios.post(`${API_BASE_URL}/api/v1/payroll/runs/`, {
                month: currentMonth,
                status: "draft"
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            Swal.fire({
                title: 'Success!',
                text: 'Payroll has been re-run successfully.',
                icon: 'success',
                confirmButtonColor: '#007bff'
            }).then(() => {
                // Navigate back to history or refresh
                navigate('/hr/payroll/history');
            });
        } catch (err) {
            console.error('Error re-running payroll:', err);
            Swal.fire({
                title: 'Error!',
                text: err.response?.data?.error || 'Failed to re-run payroll. Please try again.',
                icon: 'error',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setProcessingPayrollId(null);
        }
    };

    const handleFinalizePayroll = async () => {
        const result = await Swal.fire({
            title: 'Finalize Payroll?',
            text: 'This will finalize the payroll run. This action cannot be undone. Are you sure?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, finalize it',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        setProcessingPayrollId(id);
        try {
            const token = Cookies.get('token');
            await axios.patch(`${API_BASE_URL}/api/v1/payroll/runs/${id}/`, {
                status: "finalized"
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            Swal.fire({
                title: 'Success!',
                text: 'Payroll has been finalized successfully.',
                icon: 'success',
                confirmButtonColor: '#28a745'
            });

            // Refresh the payroll status
            const payrollRes = await axios.get(`${API_BASE_URL}/api/v1/payroll/runs/${id}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setPayrollStatus(payrollRes.data.status);
        } catch (err) {
            console.error('Error finalizing payroll:', err);
            Swal.fire({
                title: 'Error!',
                text: err.response?.data?.error || 'Failed to finalize payroll. Please try again.',
                icon: 'error',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setProcessingPayrollId(null);
        }
    };

    const getSinglePayslipHTML = (ps) => {
        return `
          <div class="printable-payslip">
            <div class="printable-watermark">
                <img src="/assets/images/burray/burray.png" alt="logo" class="logo-watermark-img" />
            </div>
            <div class="printable-header">
              <div class="company-name">${ps.employee_data?.company || 'ERP SYSTEM'}</div>
              <div class="payslip-title">Employee Payslip</div>
            </div>
            <div class="printable-section">
              <div class="section-title">Employee Information</div>
              <div class="info-grid">
                <div><strong>Employee Code:</strong> ${ps.employee_data?.employee_id || 'N/A'}</div>
                <div><strong>Full Name:</strong> ${ps.employee_data?.full_name}</div>
                <div><strong>Job Title:</strong> ${ps.employee_data?.position || 'N/A'}</div>
                <div><strong>Payroll Date:</strong> ${formatDate(ps.generated_on)}</div>
                <div><strong>Department:</strong> ${ps.employee_data?.department || 'N/A'}</div>
                <div><strong>Bank Name:</strong> ${ps.employee_data?.bank_name || 'N/A'}</div>
              </div>
            </div>
            <div class="printable-section earnings-deductions-grid">
              <div class="earnings-col">
                <div class="section-title">Earnings</div>
                <div class="item-list">
                  <div class="list-item"><span>Basic Salary</span><span>${formatCurrency(ps?.employee_data?.basic_salary || 0)}</span></div>
                  ${(ps.allowance_data && ps.allowance_data.length > 0) ?
                ps.allowance_data.map(allowance => `
                      <div class="list-item">
                        <span>${allowance.title || 'Other Allowance'}</span>
                        <span>${formatCurrency(allowance.amount || 0)}</span>
                      </div>
                    `).join('') : ''}
                </div>
                <div class="summary-item"><span>Gross Salary</span><span>${formatCurrency(ps.gross_salary || ps.employee_data?.basic_salary || 0)}</span></div>
              </div>
              <div class="deductions-col">
                <div class="section-title">Deductions</div>
                <div class="item-list">
                  ${(ps.deduction_data && ps.deduction_data.length > 0) ?
                ps.deduction_data.map(deduction => `
                      <div class="list-item">
                        <span>${deduction.title}</span>
                        <span>${formatCurrency(deduction.amount || 0)}</span>
                      </div>
                    `).join('') : ''}
                  <div class="list-item statutory"><span>NSSA</span><span>${formatCurrency(ps.nssa || 0)}</span></div>
                  <div class="list-item statutory"><span>PAYE</span><span>${formatCurrency(ps.paye || 0)}</span></div>
                  <div class="list-item statutory"><span>AIDS Levy</span><span>${formatCurrency(ps.aids_levy || 0)}</span></div>
                </div>
                <div class="summary-item"><span>Total Deductions</span><span>${formatCurrency(ps.total_deductions || 0)}</span></div>
              </div>
            </div>
            <div class="net-pay-row">
              <span class="net-pay-label">Net Payable:</span>
              <span class="net-pay-amount">${formatCurrency(ps.net_salary || 0)}</span>
            </div>
            <div class="payslip-signature">
                <div class="signature-text text-center" style="text-align: center; margin-top: 2rem; font-size: 10px; text-transform: uppercase; color: #888; font-weight: 500; font-style: italic;">burray payroll software</div>
            </div>
          </div>
        `
    }

    const getBulkPayslipHTML = (ps) => {
        return `
          <div class="printable-payslip bulk-payslip-layout">
            <div class="bulk-watermark">
                <img src="/assets/images/burray/burray.png" alt="logo" class="bulk-logo-watermark" />
            </div>
            <div class="bulk-body">
              <div class="bulk-col info-col">
                <div class="bulk-section-title">${ps.employee_data?.company || 'ERP SYSTEM'}</div>
                <div class="bulk-item"><strong>Employee No.:</strong> <span>${ps.employee_data?.employee_id || 'N/A'}</span></div>
                <div class="bulk-item"><strong>Surname:</strong> <span>${ps.employee_data?.last_name || ''}</span></div>
                <div class="bulk-item"><strong>First Name:</strong> <span>${ps.employee_data?.first_name || ''}</span></div>
                <div class="bulk-item"><strong>Job Title:</strong> <span>${ps.employee_data?.position || 'N/A'}</span></div>
                <div class="bulk-item"><strong>SSR No.:</strong> <span>${ps.employee_data?.nssa_ssr_number || 'N/A'}</span></div>
                <div class="bulk-item"><strong>TIN No.:</strong> <span>${ps.employee_data?.zimra_tin_number || 'N/A'}</span></div>
                <div class="bulk-item"><strong>Date Employed:</strong> <span>${formatDate(ps.employee_data?.start_date) || 'N/A'}</span></div>
                <div class="bulk-item"><strong>Payroll Date:</strong> <span>${formatDate(ps.generated_on)}</span></div>
                <div class="bulk-item"><strong>Department:</strong> <span>${ps.employee_data?.department || 'N/A'}</span></div>
                <div class="bulk-item"><strong>Leave Bal:</strong> <span>${ps.employee_data?.vacation_leave_days_available || 'N/A'} days</span></div>
                <div class="bulk-item"><strong>Bank & Branch:</strong> <span>${ps.employee_data?.bank_name || 'N/A'}</span></div>
                <div class="bulk-item"><strong>Account No.:</strong> <span>${ps.employee_data?.bank_account_number || 'N/A'}</span></div>
              </div>
              <div class="bulk-col earnings-col">
                <div class="bulk-section-title">Earnings</div>
                <div class="bulk-item"><span>Basic Salary:</span><span>${formatCurrency(ps?.employee_data?.basic_salary || 0)}</span></div>
                
                ${(ps.allowance_data && ps.allowance_data.length > 0) ?
                ps.allowance_data.map(allowance => `
                    <div class="bulk-item"><span>${allowance.title || 'Allowance'}:</span><span>${formatCurrency(allowance.amount || 0)}</span></div>
                  `).join('') : ''}
                ${(ps.overtime_1_5 && ps.overtime_1_5 > 0)?
                  `<div class="bulk-item"><span>Overtime(1.5x):</span><span>${formatCurrency(ps?.overtime_1_5)}</span></div>`
                  : ''
                }
                ${(ps.overtime_2_0 && ps.overtime_2_0 > 0)?
                  `<div class="bulk-item"><span>Overtime(2.0x):</span><span>${formatCurrency(ps?.overtime_2_0)}</span></div>`
                  : ''
                }
                ${(ps.cill && ps.cill > 0)?
                  `<div class="bulk-item"><span>CILL:</span><span>${formatCurrency(ps?.cill)}</span></div>`
                  : ''
                }

                
                <div class="bulk-item total"><span>Gross Salary:</span><span>${formatCurrency(ps.gross_salary || ps.employee_data?.basic_salary || 0)}</span></div>
                <div class="bulk-item total"><span>Taxable Income:</span><span>${formatCurrency(ps.taxable_income || 0)}</span></div>

                
                <div class="bulk-net-pay-wrapper">
                    <div class="bulk-item total net-pay-final">
                      <span>Net Payable:</span>
                      <span>${formatCurrency(ps.net_salary || 0)}</span>
                    </div>
                </div>
              </div>
              <div class="bulk-col deductions-col">
                <div class="bulk-section-title">Deductions</div>
                <div class="bulk-item"><span>NSSA:</span><span>${formatCurrency(ps.nssa || 0)}</span></div>
                <div class="bulk-item"><span>PAYE:</span><span>${formatCurrency(ps.paye || 0)}</span></div>
                <div class="bulk-item"><span>NEC:</span><span>${formatCurrency(ps.nec || 0)}</span></div>
                <div class="bulk-item"><span>AIDS Levy:</span><span>${formatCurrency(ps.aids_levy || 0)}</span></div>
                <div class="bulk-item"><span>ZIMDEF Levy:</span><span>${formatCurrency(ps.zimdef_levy || 0)}</span></div>
                ${(ps?.deduction_data && ps?.deduction_data.length > 0) ?
                ps.deduction_data.map(deduction => `
                    <div class="bulk-item"><span>${deduction.title}:</span><span>${formatCurrency(deduction.monthly_deduction || 0)}</span></div>
                  `).join('') : ''}
                <div class="bulk-item total"><span>Total Deductions:</span><span>${formatCurrency(ps.total_deductions || 0)}</span></div>
              </div>
            </div>
            <div class="bulk-signature">
                <div class="signature-text">Burray Payroll Software</div>
            </div>
          </div>
        `
    }

    const handlePrintPayslip = (ps) => {
        const employeeName = `${ps.employee_data?.full_name || 'Employee'}-PaySlip`.replace(/\s+/g, '_')
        const printWindow = window.open('', '_blank')
        printWindow.document.write(`
          <html>
            <head>
              <title>${employeeName}</title>
              <style>
                body { font-family: 'Arial Narrow', Arial, sans-serif; margin: 0; background: #f7fafc; color: #333; }
                .printable-payslip {
                  width: 100%; max-width: 800px; margin: 2rem auto; background: white; 
                  padding: 3rem; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); border-radius: 8px;
                }
                .printable-header { text-align: center; margin-bottom: 2rem; border-bottom: 2px solid #2563eb; padding-bottom: 1rem; }
                .company-name { font-size: 1.5rem; font-weight: 700; color: #2563eb; }
                .payslip-title { font-size: 1.2rem; font-weight: 600; color: #1e3a8a; margin-top: 0.5rem; text-transform: uppercase; }
                .section-title { font-size: 1rem; font-weight: 600; color: #2563eb; margin-bottom: 1rem; border-bottom: 1px solid #e5e7eb; padding-bottom: 0.5rem; }
                .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 2rem; }
                .earnings-deductions-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3rem; margin-bottom: 2rem; }
                .list-item { display: flex; justify-content: space-between; padding: 0.5rem 0; border-bottom: 1px solid #f1f5f9; }
                .summary-item { display: flex; justify-content: space-between; font-weight: 700; margin-top: 1rem; padding-top: 0.5rem; border-top: 1px solid #e5e7eb; }
                .net-pay-row { display: flex; justify-content: flex-end; align-items: center; gap: 2rem; font-size: 1.5rem; font-weight: 700; color: #1e3a8a; border-top: 3px solid #1e3a8a; padding-top: 1.5rem; }
                .net_pay_amount { color: #2563eb; }
                .printable-watermark {
                  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-30deg);
                  z-index: 0; pointer-events: none; opacity: 0.05;
                }
                .logo-watermark-img {
                  width: 400px; height: auto; display: block;
                }
                .printable-payslip { position: relative; }
                @media print {
                  body { background: white; }
                  .printable-payslip { box-shadow: none; margin: 0; max-width: 100%; padding: 2rem; }
                }
              </style>
            </head>
            <body>
              ${getSinglePayslipHTML(ps)}
              <script>
                window.onload = function() { setTimeout(function() { window.print(); window.close(); }, 300); };
              </script>
            </body>
          </html>
        `)
        printWindow.document.close()
    }

    const handlePrintAllPayslips = () => {
        if (!filteredPayrolls.length) return
        const payslipHTMLs = filteredPayrolls.map(ps => getBulkPayslipHTML(ps)).join('')
        const printWindow = window.open('', '_blank')
        printWindow.document.write(`
          <html>
            <head>
              <title>Bulk Payslips</title>
              <style>
                body { font-family: 'Arial Narrow', Arial, sans-serif; margin: 0; background: #f7fafc; font-size: 12px; }
                .printable-payslip {
                  width: 100%; background: white; padding: 1.5rem; box-sizing: border-box;
                  display: flex; flex-direction: column; border-bottom: 1px dashed #ccc;
                  page-break-inside: avoid; height: 33vh; position: relative;
                }
                .bulk-header { text-align: center; margin-bottom: 1rem; }
                .company-name { font-size: 1.1rem; font-weight: 700; color: #2563eb; }
                .bulk-body { display: grid; grid-template-columns: 1.2fr 1fr 1fr; gap: 1.5rem; position: relative; z-index: 1; margin-bottom: 0.5rem; }
                .bulk-col { display: flex; flex-direction: column; position: relative; }
                .bulk-section-title { font-weight: 600; color: #1e3a8a; margin-bottom: 0.5rem; font-size: 13px; }
                .bulk-item { display: flex; justify-content: space-between; margin-bottom: 7px; font-size: 10px; }
                .bulk-item.total { font-weight: 700; margin-top: 10px; padding-top: 2px; }
                .bulk-net-pay-wrapper { margin-top: 10px; padding-top: 5px; }
                .net-pay-final { background: #f8fafc; color: #1e3a8a; }
                .bulk-watermark {
                  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-30deg);
                  z-index: 0; pointer-events: none; opacity: 0.05;
                }
                .bulk-logo-watermark {
                  width: 200px; height: 150px; display: block;
                }
                .bulk-signature { margin-top: 10px; display: flex; flex-direction: column; align-items: center; justify-content: center; width: 100%; }
                .signature-text { font-size: 8px; color: #888; font-weight: 500; font-style: italic; }
                @media print {
                  body { background: white; }
                  .printable-payslip { border-bottom: 1px dashed #ccc; height: 32vh; }
                  .printable-payslip:nth-child(3n) { border-bottom: none; page-break-after: always; }
                }
               
              </style>
            </head>
            <body>
              ${payslipHTMLs}
              <script>
                window.onload = function() { setTimeout(function() { window.print(); window.close(); }, 300); };
              </script>
            </body>
          </html>
        `)
        printWindow.document.close()
    }

    if (loading) return (
        <div>
            <Topbar /> <Sidebar />
            <div className="app-content-area py-5 text-center">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-3">Loading Payslips...</p>
            </div>
        </div>
    )

    if (error) return (
        <div>
            <Topbar /> <Sidebar />
            <div className="app-content-area py-5 text-center">
                <div className="alert alert-danger mx-auto" style={{ maxWidth: '500px' }}>{error}</div>
                <button className="btn btn-primary" onClick={() => navigate(-1)}>Back to History</button>
            </div>
        </div>
    )

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
            <Topbar />
            <Sidebar />
            <div className="app-content-area">
                <div className="app-content-wrap">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-xl-12">
                                <div className="card">
                                    <div className="card-header justify-between flex-wrap gap-15">
                                        <div className="d-flex-items gap-10">
                                            <h4 className="mb-0">Payslips for Payroll Run #{id}</h4>
                                        </div>
                                        <div className="d-flex flex-wrap gap-15 align-items-center">
                                            <div className="search-input-wrapper position-relative">
                                                <input
                                                    type="text"
                                                    placeholder="Search employee name or ID..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    className="form-control form-control-sm ps-30"
                                                    style={{ minWidth: '250px' }}
                                                />
                                                <RiSearchLine className="position-absolute start-10 top-50 translate-middle-y text-muted" />
                                            </div>
                                            <button className="btn btn-primary btn-sm d-flex-items gap-5 text-white" onClick={handlePrintAllPayslips}>
                                                <RiPrinterLine /> Print All
                                            </button>
                                            <a className="btn btn-success btn-sm text-white" href="javascript:void(0);">Export Excel</a>
                                            
                                        </div>
                                    </div>
                                    <div className="card-body pt-15">
                                        <div className="table-responsive custom--scrollbar">
                                            <table className="table text-nowrap w-100 fs-14">
                                                <thead className="bg-light-subtle">
                                                    <tr>
                                                        <th>Employee Name</th>
                                                        <th className="text-end">Basic Salary</th>
                                                        <th className="text-end">Allowances</th>
                                                        <th className="text-end">Deductions</th>
                                                        <th className="text-end">Net Salary</th>
                                                        <th>Payroll Date</th>
                                                        <th className="text-center">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredPayrolls.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="7" className="text-center py-4 text-muted">
                                                                {searchTerm ? "No employees match your search." : "No payslips found for this cycle."}
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        filteredPayrolls.map((ps) => (
                                                            <tr key={ps.id}>
                                                                <td>
                                                                    <div className="d-flex flex-column">
                                                                        <h6 className="mb-0"><a href={`/hr/employee/${ps.employee?.id}`}>{ps.employee_data?.first_name} {ps.employee_data?.last_name}</a></h6>
                                                                        <small className="text-muted">{ps.employee_data?.employee_id}</small>
                                                                    </div>
                                                                </td>
                                                                <td className="text-end">{formatCurrency(ps.employee_data?.basic_salary)}</td>
                                                                <td className="text-end">{formatCurrency(ps.total_allowances || 0)}</td>
                                                                <td className="text-end">{formatCurrency(ps.total_deductions || 0)}</td>
                                                                <td className="text-end fw-bold text-primary">{formatCurrency(ps.net_salary)}</td>
                                                                <td>{formatDate(ps.generated_on)}</td>
                                                                <td className="text-center">
                                                                    <div className="d-flex-items gap-5 justify-content-center">
                                                                        <button
                                                                            className="btn-icon btn-success-light"
                                                                            onClick={() => navigate(`/hr/payroll/payslip-detail/${ps.id}`)}
                                                                            title="View Detail"
                                                                        >
                                                                            <RiEye2Line />
                                                                        </button>
                                                                        
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                .ps-30 { padding-left: 30px !important; }
                .start-10 { left: 10px !important; }
                .fs-14 { font-size: 14px; }
            `}</style>
        </div>
    )
}

export default Payslips
